import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';

import { useThemeMode } from '@/context';
import { useAuth } from '@/features/auth';
import { COLLAPSED_W, DRAWER_W } from '@/features/admin/constants/layout';
import { createAdminTheme } from '@/features/admin/constants/theme';
import { AdminLayoutContext } from '@/features/admin/context/AdminLayoutContext';
import { useSidebarState } from '@/features/admin/hooks/useSidebarState';

import { AdminSidebar } from './AdminSidebar';

export const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode();
  const adminTheme = useMemo(() => createAdminTheme(themeMode === 'dark' ? 'dark' : 'light'), [themeMode]);
  const isMobile = useMediaQuery(adminTheme.breakpoints.down('md'));
  const { user } = useAuth();
  const { sidebarOpen, setSidebarOpen, mobileOpen, setMobileOpen } = useSidebarState();

  const drawerWidth = sidebarOpen ? DRAWER_W : COLLAPSED_W;
  const paperSx = {
    boxSizing: 'border-box' as const,
    bgcolor: 'background.default',
    borderRight: '1px solid',
    borderColor: 'divider',
    backgroundImage: 'none',
  };

  const sidebar = (
    <AdminSidebar
      sidebarOpen={isMobile ? true : sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      onMobileClose={() => setMobileOpen(false)}
      user={user}
    />
  );

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline enableColorScheme />
      <AdminLayoutContext.Provider value={{
        onMobileMenuClick: () => setMobileOpen((o) => !o),
        themeMode,
        setThemeMode,
      }}>
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.paper', color: 'text.primary', fontFamily: adminTheme.typography.fontFamily }}>
          <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: 'width 200ms ease' }}>
            {isMobile ? (
              <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { ...paperSx, width: DRAWER_W } }}>
                {sidebar}
              </Drawer>
            ) : (
              <Drawer variant="permanent" open sx={{ '& .MuiDrawer-paper': { ...paperSx, width: drawerWidth, overflowX: 'hidden', transition: 'width 200ms ease' } }}>
                {sidebar}
              </Drawer>
            )}
          </Box>
          <Box component="main" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {children}
          </Box>
        </Box>
      </AdminLayoutContext.Provider>
    </ThemeProvider>
  );
};
